import i18next from 'i18next';
import { LOCK_PROVIDERS, ROLE_MAP, SMART_LOCKER_PROVIDERS } from './mappings';
import moment from 'moment';
import { DATE_FORMATS, ORGANIZATION_PROVIDER_PROPERTY_TYPE } from '../views/users/constant';
import { ZMOD_PROD_PROJECT_ID } from '../helper/constants';

export function getRoleNameByID(roleID) {
  const parsedRoleID = parseInt(roleID);
  return ROLE_MAP.find((type) => type.id === parsedRoleID)?.value || undefined;
}

export function getLockProviderNameByID(lockProviderId) {
  const parsedLockProviderID = parseInt(lockProviderId);
  return LOCK_PROVIDERS.find((type) => type.id === parsedLockProviderID)?.value || undefined;
}

export function getSmartLockerProviderNameByID(smartLockerProviderId) {
  const parsedSmartLockerProviderID = parseInt(smartLockerProviderId);
  return SMART_LOCKER_PROVIDERS.find((type) => type.id === parsedSmartLockerProviderID)?.value || undefined;
}

export function getRoomUnitTerminology(config) {
  return config?.suite_enabled ? i18next.t('Room') : i18next.t('Unit');
}

// TODO: This function is temporary, in the future all options will be placed inside of the filters object
export function getAllSearchOptions(searchState) {
  return {
    search: searchState.search,
    minCreationDate: searchState.minCreationDate,
    maxCreationDate: searchState.maxCreationDate,
    category: searchState.category,
    building_id: searchState.building_id,

    filters: searchState.filters,
    ordering: searchState.ordering,
    pagination: searchState.pagination
  };
}

export function getFilterCount(searchState) {
  function countValidItems(value) {
    if (value === null || value === undefined) {
      return 0;
    }
    if (Array.isArray(value)) {
      return value.reduce((sum, item) => sum + countValidItems(item), 0);
    }
    if (typeof value === 'object') {
      return Object.values(value).reduce((sum, item) => sum + countValidItems(item), 0);
    }
    return 1;
  }

  return Object.values(searchState).reduce((sum, value) => sum + countValidItems(value), 0);
}

export function formatMbFileSize(size) {
  return Number((size / (1024 * 1024)).toFixed(2));
}

export const getUTCTimestampForLocalDay = (localDate) => {
  if (!localDate) return undefined;

  const date = moment(localDate).startOf('day');
  return date.utc().format(DATE_FORMATS.YYYY_MM_DD_hh_mm_ss_A);
};

export const parseUnitNumber = (unitNum) => {
  if (!unitNum) return { number: 0, suffix: '' };

  // Since we don't have a standard way of formatting units/suites, this regex captures the most commonly used formats such as:
  // A103, A-103, 103A, 103-A, B-42-C, B42C
  const match = unitNum.match(/^([A-Z])?-?(\d+)-?([A-Z])?$/i);
  if (!match) return { number: 0, suffix: '' };

  return {
    prefix: (match[1] || '').toUpperCase(),
    number: parseInt(match[2]),
    suffix: (match[3] || '').toUpperCase()
  };
};

export const getFileExtension = (url) => {
  const parts = url.split('.');
  return parts[parts.length - 1].split('?')[0];
};

export const safeJsonParse = (jsonString) => {
  if (!jsonString) return null;

  try {
    const parsed = JSON.parse(jsonString);
    return parsed || null;
  } catch (error) {
    console.error('Error parsing JSON:', error);
    return null;
  }
};

export const formatMoney = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency
  }).format(amount);
};

export const calculateNextPaymentDate = (currentDate, frequency) => {
  if (currentDate === null || frequency === null) return null;
  const date = moment(currentDate);

  if (!date.isValid()) {
    return null;
  }

  const nextDate = date.clone();
  switch (frequency.toLowerCase()) {
    case 'single':
      return null;

    case 'weekly':
      nextDate.add(1, 'week');
      break;

    case 'monthly':
      nextDate.add(1, 'month');
      break;

    case 'yearly':
      nextDate.add(1, 'year');
      break;

    default:
      return null;
  }

  return nextDate;
};

export const isEntrataPropertyProvider = (organization) => {
  return organization && organization.prov_property === ORGANIZATION_PROVIDER_PROPERTY_TYPE.ENTRATA;
};

export const isZModProdEnvironment = () => {
  return process.env.REACT_APP_PROJECT_ID === ZMOD_PROD_PROJECT_ID;
};

export const defaultTemperatureRange = {
  heat: [41, 110],
  cool: [46, 110],
};

export const defaultTemperatureRangeCelsius = {
  heat: [5, 42],
  cool: [8, 42],
}

export const defaultTemperatureInputRanges = {
  heat: [57, 95],
  cool: [46, 101],
}

export const defaultTemperatureInputRangesCelsius = {
  heat: [14, 35],
  cool: [15, 38],
}

export const honeywellTemperatureRanges = {
  F: {
    heat: [45, 101],
    cool: [45, 91],
  },
  C: {
    heat: [7, 38], 
    cool: [7, 33], 
  }
}

export const mysaTemperatureRanges = {
  F: {
    heat: [41, 86],
    cool: [41, 86],
  },
  C: {
    heat: [5, 30], 
    cool: [5, 30], 
  }
}

export const defaultSetPoint = {
  occupied: 65,
  vacant: 60,
}

export const defaultSetPointCelsius = {
  occupied: 18,
  vacant: 16,
}

export const capitalizeFirstLetter = (str) => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};